/* Snapping + smart guides. Writes state.snapInd / state.guides as it goes;
   the renderer draws them. */
import type { Pt, Wall } from './types.ts';
import { state, keys } from './state.ts';
import { angDiff, clamp, parseLen, rad } from './utils.ts';
import { distToSeg, wallPt, wallVec, wallsAt } from './geom.ts';

export const snapV = (v: number) => state.snapStep ? Math.round(v / state.snapStep) * state.snapStep : v;

export function endpointSnap(p: Pt, excludeIds?: Set<string>) {
  const tol = 9 / state.view.zoom;
  for (const w of state.doc.walls) {
    if (excludeIds && excludeIds.has(w.id)) continue;
    for (const [x, y] of [[w.x1, w.y1], [w.x2, w.y2]] as const)
      if (Math.hypot(p.x - x, p.y - y) < tol) return { x, y, snapped: true };
  }
  return null;
}

/** Snap onto another wall's exact midpoint (⋈ tie candidate). */
export function midSnap(p: Pt, excludeIds?: Set<string>) {
  const tol = 9 / state.view.zoom;
  for (const w of state.doc.walls) {
    if (excludeIds && excludeIds.has(w.id)) continue;
    const mx = (w.x1 + w.x2) / 2, my = (w.y1 + w.y2) / 2;
    if (Math.hypot(p.x - mx, p.y - my) < tol) return { x: mx, y: my, wall: w, snapped: true };
  }
  return null;
}

/** Snap onto a wall's centerline (T-junctions); t tidied to the grid. */
export function edgeSnap(p: Pt, excludeIds?: Set<string>) {
  const tol = 9 / state.view.zoom;
  let best: { wall: Wall; t: number; d: number } | null = null;
  for (const w of state.doc.walls) {
    if (excludeIds && excludeIds.has(w.id)) continue;
    const r = distToSeg(p.x, p.y, w.x1, w.y1, w.x2, w.y2);
    if (r.d <= tol && (!best || r.d < best.d)) best = { wall: w, t: r.t, d: r.d };
  }
  if (!best) return null;
  const t = clamp(snapV(best.t), 0, wallVec(best.wall).len);
  const q = wallPt(best.wall, t);
  return { x: q.x, y: q.y, wall: best.wall, snapped: true };
}

export function snapPt(p: Pt, excludeIds?: Set<string>): Pt {
  const es = endpointSnap(p, excludeIds);
  if (es) { state.snapInd = { type: 'end', x: es.x, y: es.y }; return es; }
  const md = midSnap(p, excludeIds);
  if (md) { state.snapInd = { type: 'mid', x: md.x, y: md.y, wall: md.wall }; return md; }
  const ed = edgeSnap(p, excludeIds);
  if (ed) { state.snapInd = { type: 'edge', x: ed.x, y: ed.y, wall: ed.wall }; return ed; }
  return { x: snapV(p.x), y: snapV(p.y) };
}

/** Dashed alignment guide + snap of one coordinate to another wall endpoint. */
export function axisAlign(q: Pt, excludeIds?: Set<string> | null, lockX?: boolean, lockY?: boolean): Pt {
  const tol = 6 / state.view.zoom;
  let bx: Pt | null = null, by: Pt | null = null;
  for (const w of state.doc.walls) {
    if (excludeIds && excludeIds.has(w.id)) continue;
    for (const [ex, ey] of [[w.x1, w.y1], [w.x2, w.y2]] as const) {
      if (!lockX && Math.abs(q.x - ex) < tol && (!bx || Math.abs(q.x - ex) < Math.abs(q.x - bx.x))) bx = { x: ex, y: ey };
      if (!lockY && Math.abs(q.y - ey) < tol && (!by || Math.abs(q.y - ey) < Math.abs(q.y - by.y))) by = { x: ex, y: ey };
    }
  }
  if (bx) { q.x = bx.x; state.guides.push({ kind: 'line', x1: bx.x, y1: bx.y, x2: bx.x, y2: q.y }); }
  if (by) { q.y = by.y; state.guides.push({ kind: 'line', x1: by.x, y1: by.y, x2: q.x, y2: by.y }); }
  return q;
}

/** Walls the point hangs off of: joined at a corner OR hosting a T-junction
    (the point sits mid-span on their centerline). These are the references
    for perpendicular/parallel angle snaps + highlights. */
function refWallsAt(at: Pt, excl?: Set<string>): Wall[] {
  const out = wallsAt(state.doc.walls, at, excl);
  for (const w of state.doc.walls) {
    if ((excl && excl.has(w.id)) || out.includes(w)) continue;
    if (distToSeg(at.x, at.y, w.x1, w.y1, w.x2, w.y2).d < 1) out.push(w);
  }
  return out;
}

/** When a dragged/drawn end lands on `host` and the segment from `from` is
    nearly perpendicular to it, prefer the exact ⊥ foot — the wall then meets
    the host dead square, and the drop can record that as a constraint. */
function perpFoot(host: Wall, from: Pt, mouse: Pt): Pt | null {
  const hv = wallVec(host);
  const t = clamp((from.x - host.x1) * hv.ux + (from.y - host.y1) * hv.uy, 0, hv.len);
  const q = { x: host.x1 + hv.ux * t, y: host.y1 + hv.uy * t };
  const reach = Math.hypot(q.x - from.x, q.y - from.y);
  if (reach < 1) return null;
  const rawA = Math.atan2(mouse.y - from.y, mouse.x - from.x);
  const footA = Math.atan2(q.y - from.y, q.x - from.x);
  return angDiff(rawA - footA) < rad(4) ? q : null;
}

/** Highlight the reference wall + a ⊥/∥ badge beside the moving wall. */
function refGuide(ref: Wall, rel: '⊥' | '∥', fixed: Pt, end: Pt): void {
  const dx = end.x - fixed.x, dy = end.y - fixed.y;
  const len = Math.hypot(dx, dy) || 1;
  const off = 14 / state.view.zoom + 4;
  state.guides.push({
    kind: 'ref', wall: ref, rel,
    bx: fixed.x + dx * 0.6 - dy / len * off,
    by: fixed.y + dy * 0.6 + dx / len * off,
  });
}

/** ⊥ / ∥ glyphs for the joint at `at` when the new direction relates to a neighbor. */
export function jointGuides(at: Pt, ang: number, excludeIds?: Set<string>): void {
  for (const nb of wallsAt(state.doc.walls, at, excludeIds)) {
    const na = Math.atan2(nb.y2 - nb.y1, nb.x2 - nb.x1);
    const s = Math.abs(Math.sin(ang - na));
    if (s > .999) {
      // leg must point from the corner INTO the neighbor wall
      const a2 = Math.hypot(nb.x1 - at.x, nb.y1 - at.y) < .75 ? na : na + Math.PI;
      state.guides.push({ kind: 'perp', x: at.x, y: at.y, a1: ang, a2 });
    }
  }
}

/** Draft endpoint + which snap guided it (ref/rel/edge/mid feed draw-time auto-constraints). */
export type DraftEnd = Pt & { ref?: Wall; rel?: '⊥' | '∥'; edge?: Wall; mid?: Wall };

export function wallDraftEnd(d: { ax: number; ay: number; typed: string }, m: Pt): DraftEnd {
  const es = endpointSnap(m);
  if (es) { state.snapInd = { type: 'end', x: es.x, y: es.y }; return es; }
  if (d.typed !== '') {
    const len = parseLen(d.typed, 'ft');
    if (len && len > 0) {
      const ang = Math.round(Math.atan2(m.y - d.ay, m.x - d.ax) / (Math.PI / 4)) * (Math.PI / 4);
      return { x: d.ax + Math.cos(ang) * len, y: d.ay + Math.sin(ang) * len };
    }
  }
  if (keys.alt) return snapPt(m);
  const md = midSnap(m);
  if (md) {
    state.snapInd = { type: 'mid', x: md.x, y: md.y, wall: md.wall };
    return { x: md.x, y: md.y, mid: md.wall };    // ⋈ tie onto the host's middle
  }
  const ed = edgeSnap(m);
  if (ed) {
    const start = { x: d.ax, y: d.ay };
    const q = perpFoot(ed.wall, start, m);
    if (q) {
      state.snapInd = { type: 'edge', x: q.x, y: q.y, wall: ed.wall };
      refGuide(ed.wall, '⊥', start, q);
      return { x: q.x, y: q.y, edge: ed.wall };   // dead-square T-junction
    }
    state.snapInd = { type: 'edge', x: ed.x, y: ed.y, wall: ed.wall };
    return { x: ed.x, y: ed.y, edge: ed.wall };   // T-junction: remember the host
  }
  const dx = m.x - d.ax, dy = m.y - d.ay;
  const raw = Math.atan2(dy, dx);
  // candidate angles: 45° steps plus perpendicular/parallel to walls the
  // start hangs off (corner-joined or T-host); ties go to the wall reference
  let ang = Math.round(raw / (Math.PI / 4)) * (Math.PI / 4);
  let ref: Wall | null = null, rel: '⊥' | '∥' = '⊥';
  const start = { x: d.ax, y: d.ay };
  for (const nb of refWallsAt(start)) {
    const na = Math.atan2(nb.y2 - nb.y1, nb.x2 - nb.x1);
    for (const off of [Math.PI / 2, -Math.PI / 2, 0, Math.PI]) {
      if (angDiff(raw - (na + off)) <= angDiff(raw - ang) + 1e-9) {
        ang = na + off; ref = nb; rel = off === 0 || off === Math.PI ? '∥' : '⊥';
      }
    }
  }
  const c = Math.cos(ang), s = Math.sin(ang);
  let end: DraftEnd;
  if (Math.abs(c) < 1e-9)      end = axisAlign({ x: d.ax, y: snapV(m.y) }, null, true, false);
  else if (Math.abs(s) < 1e-9) end = axisAlign({ x: snapV(m.x), y: d.ay }, null, false, true);
  else {
    const L = Math.max(state.snapStep || 1, snapV(Math.hypot(dx, dy)));
    end = { x: d.ax + c * L, y: d.ay + s * L };
  }
  jointGuides(start, Math.atan2(end.y - d.ay, end.x - d.ax));
  if (ref && angDiff(Math.atan2(end.y - d.ay, end.x - d.ax) - ang) < 1e-6) {
    refGuide(ref, rel, start, end);
    end.ref = ref; end.rel = rel;
  }
  return end;
}

/**
 * Dragging a wall endpoint: join snaps first, then soft angle constraints
 * (H/V and perpendicular/parallel to walls joined at the fixed end), then
 * grid + alignment to other wall endpoints. lockLen: the wall's length is
 * locked — only the angle may change, so join/grid snaps are skipped.
 */
export function wallEndSnap(p: Pt, fixed: Pt, excl: Set<string>, lockLen: number): Pt {
  // ⌥: free drag — no join/midpoint/edge/⊥∥/axis/grid snapping at all.
  // (Constraints still hold; they are rules, not snaps. A length lock keeps
  // the end on its arc, at the raw mouse angle.)
  if (keys.alt) {
    if (!lockLen) return { x: p.x, y: p.y };
    const raw = Math.atan2(p.y - fixed.y, p.x - fixed.x);
    return { x: fixed.x + Math.cos(raw) * lockLen, y: fixed.y + Math.sin(raw) * lockLen };
  }
  if (!lockLen) {
    const es = endpointSnap(p, excl);
    if (es) { state.snapInd = { type: 'end', x: es.x, y: es.y }; return es; }
    const md = midSnap(p, excl);
    if (md) { state.snapInd = { type: 'mid', x: md.x, y: md.y, wall: md.wall }; return md; }
    const ed = edgeSnap(p, excl);
    if (ed) {
      const q = perpFoot(ed.wall, fixed, p);
      if (q) {
        state.snapInd = { type: 'edge', x: q.x, y: q.y, wall: ed.wall };
        refGuide(ed.wall, '⊥', fixed, q);
        return q;
      }
      state.snapInd = { type: 'edge', x: ed.x, y: ed.y, wall: ed.wall };
      return ed;
    }
  }
  const raw = Math.atan2(p.y - fixed.y, p.x - fixed.x);
  const L = Math.hypot(p.x - fixed.x, p.y - fixed.y);
  let best: { ang: number; diff: number; ref?: Wall; rel?: '⊥' | '∥' } | null = null;
  const consider = (cand: number, ref?: Wall, rel?: '⊥' | '∥') => {
    const diff = angDiff(raw - cand);
    if (diff < rad(3.5) && (!best || diff < best.diff)) best = { ang: cand, diff, ref, rel };
  };
  // neighbors first: on a tie (axis-aligned neighbor) the wall reference wins,
  // so the relation still gets highlighted
  for (const nb of refWallsAt(fixed, excl)) {
    const na = Math.atan2(nb.y2 - nb.y1, nb.x2 - nb.x1);
    for (const off of [Math.PI / 2, -Math.PI / 2, 0, Math.PI])
      consider(na + off, nb, off === 0 || off === Math.PI ? '∥' : '⊥');
  }
  for (let k = 0; k < 4; k++) consider(k * Math.PI / 2);
  // (assertion: TS can't see the `consider` closure assigning `best`)
  const chosen = best as { ang: number; diff: number; ref?: Wall; rel?: '⊥' | '∥' } | null;
  if (chosen || lockLen) {
    const ang = chosen ? chosen.ang : raw;
    const len = lockLen || Math.max(1, snapV(L));
    const q = { x: fixed.x + Math.cos(ang) * len, y: fixed.y + Math.sin(ang) * len };
    if (chosen) {
      jointGuides(fixed, chosen.ang, excl);
      if (chosen.ref) refGuide(chosen.ref, chosen.rel!, fixed, q);
      if (!state.guides.length)   // plain H/V lock: show the axis as a dashed line
        state.guides.push({ kind: 'line', x1: fixed.x, y1: fixed.y, x2: q.x, y2: q.y });
    }
    return q;
  }
  return axisAlign({ x: snapV(p.x), y: snapV(p.y) }, excl);
}
